/**
 * Runtime Kernel —— 引擎启动、实验包加载、模块生命周期、全局状态和事件总线。
 * 对应技术设计 §4.1，接口签名与文档 `:217-226` 完全一致，不加不减。
 *
 * P0 范围：load / start / pause / reset / dispose + 三种模式。
 * 交互（P1）、规则与 Mission（P2）、动画（P3）、评价（P4）都还没有接入，
 * 引擎此时的产出就是「装配图 + 场景 + 渲染」，即 §21.1 P0 的验收：能加载一个实验包并显示器材。
 */

import type { ExperimentPack, LabMode } from "../data/schemas.js";
import type { AssemblyGraph } from "../eom/assembly-graph.js";
import { createAssemblyGraph, updateObject } from "../eom/assembly-graph.js";
import type { EvalContext } from "../logic/condition-evaluator.js";
import type { CompiledRule, RuleDefinition } from "../logic/rule-engine.js";
import { compileRules, runRules } from "../logic/rule-engine.js";
import type { ProcessDefinition, ProcessTable } from "../logic/process-engine.js";
import { createProcesses, derivedStateWrites, tickProcesses } from "../logic/process-engine.js";
import {
  appendSafety,
  checkSafety,
  createSafetyLog,
  selectSafetyRules,
  type SafetyLog,
} from "../logic/safety-engine.js";
import { MissionEngine, type MissionDefinition } from "../mission/mission-engine.js";
import {
  KnowledgeEngine,
  findMissingKnowledgeIds,
  type KnowledgeDocument,
} from "../knowledge/knowledge-engine.js";
import type { Hint } from "../mission/hint-engine.js";
import {
  findUnsupportedPhenomena,
  listSupportedPhenomena,
} from "../animation/animation-engine.js";
import { SceneManager, type Scene } from "../scene/scene-manager.js";
import { EventBus } from "./event-bus.js";
import { Store, type EngineState } from "./store.js";

export interface LabEngineApi {
  load(pack: ExperimentPack): Promise<void>;
  start(mode: LabMode): void;
  pause(): void;
  reset(): void;
  dispose(): void;
}

/** 引擎内部事件名。**不是出网事件**——出网事件目录在 S0 才建（§18.2 / §21.3）。 */
export const ENGINE_EVENTS = {
  packLoaded: "engine.pack.loaded",
  started: "engine.started",
  paused: "engine.paused",
  reset: "engine.reset",
  disposed: "engine.disposed",
  modeChanged: "engine.mode.changed",
  stepPassed: "mission.step.passed",
  stepFailed: "mission.step.failed",
  stepBlocked: "mission.step.blocked",
  missionCompleted: "mission.completed",
  safety: "safety.violation",
  knowledge: "knowledge.triggered",
  animate: "animation.requested",
} as const;

/** rules.json 的顶层结构（§6.2 + 本任务补入的 processes 段）。 */
interface RulesDocument {
  schemaVersion?: string;
  processes?: ProcessDefinition[];
  rules?: RuleDefinition[];
}

/** 一次 settle 之后引擎对外暴露的运行时快照。 */
export interface RuntimeSnapshot {
  stepId: string | null;
  stepIndex: number;
  stepCount: number;
  completed: boolean;
  hint: Hint | null;
  progress: { met: number; total: number };
  processes: ProcessTable;
  safety: SafetyLog;
  /** 本轮要播的现象（M1c 的 animation-engine 消费） */
  animations: string[];
  /** 本轮要弹的知识点（M1c 的 knowledge-engine 消费） */
  knowledge: string[];
}

export class LabEngine implements LabEngineApi {
  readonly bus = new EventBus();
  readonly store = new Store();

  private readonly sceneManager = new SceneManager();
  private pack: ExperimentPack | null = null;

  /* ── 逻辑层运行时（M1b）。全部由实验包驱动，引擎不内置任何具体实验 ── */
  private rules: CompiledRule[] = [];
  private safetyRules: CompiledRule[] = [];
  private processDefs: ProcessDefinition[] = [];
  private processes: ProcessTable = {};
  private domainState: Record<string, Record<string, unknown>> = {};
  private events = new Set<string>();
  private mission: MissionEngine | null = null;
  private knowledge = new KnowledgeEngine([]);
  private safetyLog: SafetyLog = createSafetyLog();
  private firedOnce = new Set<string>();
  private firedSafety = new Set<string>();
  private startedAtMs = 0;
  private lastTickMs = 0;

  async load(pack: ExperimentPack): Promise<void> {
    this.pack = pack;
    const { scene, graph } = this.buildFromPack(pack);
    this.buildLogic(pack);

    this.store.update({
      status: "loaded",
      packId: pack.id,
      packTitle: pack.title,
      scene,
      graph,
    });

    this.bus.emit(ENGINE_EVENTS.packLoaded, {
      packId: pack.id,
      objectCount: Object.keys(graph.nodes).length,
      zones: scene.zones.map((zone) => zone.id),
    });
  }

  /**
   * 编译实验包里的规则、过程与 Mission。
   *
   * 任何一条条件写错都会在这里抛错 —— **加载期失败好过运行期静默**：
   * 静默的表现是「某一步永远过不了」，作者无从排查（红线：条件解析失败必须抛错）。
   */
  private buildLogic(pack: ExperimentPack): void {
    const ruleDocs = pack.rules
      .map((spec) => spec.resolved as RulesDocument | undefined)
      .filter((doc): doc is RulesDocument => Boolean(doc));

    const definitions = ruleDocs.flatMap((doc) => doc.rules ?? []);
    this.processDefs = ruleDocs.flatMap((doc) => doc.processes ?? []);
    this.rules = compileRules(definitions);
    this.safetyRules = selectSafetyRules(this.rules);
    this.processes = createProcesses(this.processDefs);

    const missionDoc = pack.mission.resolved as MissionDefinition | undefined;
    this.mission = missionDoc ? new MissionEngine(missionDoc, 0) : null;

    this.knowledge = new KnowledgeEngine(
      pack.knowledge
        .map((spec) => spec.resolved as KnowledgeDocument | undefined)
        .filter((doc): doc is KnowledgeDocument => Boolean(doc)),
    );

    // 引用了但没定义的知识点，加载期就要报出来。
    // 静默的话学生会遇到「触发了却什么都没弹」—— bug/023 第 3 条的体感来源之一。
    const referenced = [
      ...definitions.flatMap((rule) =>
        rule.then.flatMap((action) => ("knowledge" in action ? [action.knowledge] : [])),
      ),
      ...(missionDoc?.steps ?? []).flatMap((step) => step.knowledgeOnError ?? []),
    ];
    const missing = findMissingKnowledgeIds(this.knowledge, referenced);
    if (missing.length > 0) {
      throw new Error(
        `实验包引用了未定义的知识点：${missing.join(", ")}。请在 knowledge.json 里补上，或去掉引用。`,
      );
    }

    /*
     * 能力对账闸门（`bug/023-化学/002-bug.md` 的系统性根因）。
     *
     * 那次手工测试暴露的不是三个孤立 bug，而是一个模式：**实验包声明了引擎从未实现
     * 的东西，而且没有任何机制会发现**。当时同时存在三处：
     *   modeDefaults.showGhost  → 引擎零消费
     *   6 个 animate            → 无播放器，事件被丢弃
     *   knowledge.json          → 空壳
     * 每一处都伪装成「功能做了」，只能靠人工点一遍才发现。
     *
     * 所以：包请求的现象必须在引擎的 SUPPORTED_PHENOMENA 里。对不上就拒绝加载，
     * 不再让「声明了但没实现」这件事静默通过。
     */
    const phenomena = definitions.flatMap((rule) =>
      rule.then.flatMap((action) => ("animate" in action ? [action.animate] : [])),
    );
    const unsupported = findUnsupportedPhenomena(phenomena);
    if (unsupported.length > 0) {
      throw new Error(
        `实验包请求了引擎未实现的现象：${unsupported.join(", ")}。` +
          `引擎当前支持：${listSupportedPhenomena().join(", ")}。` +
          `要么改用已支持的现象，要么先在 animation-engine 里把它真正实现出来 —— 不要留占位。`,
      );
    }

    this.domainState = {};
    this.events = new Set();
    this.safetyLog = createSafetyLog();
    this.firedOnce = new Set();
    this.firedSafety = new Set();
  }

  start(mode: LabMode, now = 0): void {
    this.requireLoaded("start");
    this.startedAtMs = now;
    this.lastTickMs = now;
    this.store.update({ status: "running", mode });
    this.bus.emit(ENGINE_EVENTS.started, { mode });
  }

  pause(): void {
    if (this.store.getState().status !== "running") return;
    this.store.update({ status: "paused" });
    this.bus.emit(ENGINE_EVENTS.paused, {});
  }

  /** 回到 load 之后的状态：重建器材与布局、清空关系。**不重新取包**。 */
  reset(): void {
    const pack = this.requireLoaded("reset");
    const { scene, graph } = this.buildFromPack(pack);
    // 逻辑层也要一起重建：只重置图会留下已触发的 once 规则与已通过的步骤，
    // 表现为「重置后实验直接跳到一半」。
    this.buildLogic(pack);
    this.lastTickMs = this.startedAtMs;
    this.store.update({ status: "loaded", scene, graph });
    this.bus.emit(ENGINE_EVENTS.reset, { packId: pack.id });
  }

  setMode(mode: LabMode): void {
    if (this.store.getState().mode === mode) return;
    this.store.update({ mode });
    this.bus.emit(ENGINE_EVENTS.modeChanged, { mode });
  }

  dispose(): void {
    this.bus.emit(ENGINE_EVENTS.disposed, {});
    this.sceneManager.dispose();
    this.pack = null;
    this.store.update({ status: "disposed", graph: null, scene: null, packId: null, packTitle: null });
    this.store.clearListeners();
    this.bus.clear();
  }

  getState(): EngineState {
    return this.store.getState();
  }

  getGraph(): AssemblyGraph {
    const graph = this.store.getState().graph;
    if (!graph) throw new Error("LabEngine.getGraph() 必须在 load() 之后调用");
    return graph;
  }

  /**
   * 写回装配图。交互层（拖拽/连接）唯一的写入口。
   *
   * 图本身是不可变的（`assembly-graph.ts` 全是纯函数），所以这里只是把新引用挂上去；
   * 引擎不校验图的内容 —— 语义合法性由 interaction 层的 `anchorsCompatible` +
   * `resolveBond` 保证（§22 红线 1），不在这里做第二道判断，避免两处判据漂移。
   */
  setGraph(graph: AssemblyGraph): void {
    if (this.store.getState().graph === graph) return;
    this.store.update({ graph });
  }

  /* ────────────────────────────────────────────────────────────────
   * 逻辑循环
   *
   * 一次 settle 干四件事，顺序不能换：
   *   ① 推进过程（时间轴）→ ② 过程进度写回器材 state
   *   → ③ 跑规则（状态可能再变）→ ④ 判定 Mission 步骤
   *
   * 规则必须在 Mission 判定**之前**：步骤的 success 条件读的是规则写出来的状态
   * （比如「温度 ≥ 200」由 heating 过程驱动、「sealed == true」由规则置位）。
   * 顺序反了会导致学生操作正确但要等下一帧才通过，表现为「反应迟钝」。
   * ──────────────────────────────────────────────────────────────── */

  /** 交互层每次改图后调用；动画帧也调用（推进过程）。`now` 是相对毫秒。 */
  settle(now: number): RuntimeSnapshot {
    const graph = this.store.getState().graph;
    if (!graph) return this.emptySnapshot();

    const elapsed = Math.max(0, now - this.lastTickMs);
    this.lastTickMs = now;

    // ① 过程推进
    const ticked = tickProcesses(this.processDefs, this.processes, elapsed);
    this.processes = ticked.processes;

    // ② 过程进度写回器材 state
    let nextGraph = graph;
    for (const write of derivedStateWrites(this.processDefs, this.processes)) {
      const match = /^object\(([^)]+)\)\.state\.(.+)$/.exec(write.set);
      if (!match) continue;
      const objectId = match[1]!.trim();
      const key = match[2]!.trim();
      const node = nextGraph.nodes[objectId];
      if (!node) continue;
      nextGraph = updateObject(nextGraph, objectId, {
        state: { ...node.state, [key]: write.value },
      });
    }

    const stepId = this.mission?.currentStep()?.id;
    const baseContext: EvalContext = {
      graph: nextGraph,
      state: this.domainState,
      processes: this.processes,
      events: this.events,
    };

    // ③ 规则
    const outcome = runRules(this.rules, baseContext, {
      ...(stepId !== undefined ? { stepId } : {}),
      firedOnce: this.firedOnce,
    });
    for (const id of outcome.firedRuleIds) this.firedOnce.add(id);
    this.domainState = outcome.state;
    this.processes = outcome.processes;
    nextGraph = outcome.graph;

    for (const emitted of outcome.emitted) {
      this.events.add(emitted.emit);
      this.bus.emit(emitted.emit, emitted.payload ?? {});
    }
    for (const action of outcome.animations) {
      this.bus.emit(ENGINE_EVENTS.animate, { id: action.animate, params: action.params });
    }
    for (const action of outcome.knowledge) {
      this.bus.emit(ENGINE_EVENTS.knowledge, { id: action.knowledge, source: "rule" });
    }

    // ③b 安全检查（复用规则表里带 severity 的那些）
    const safetyContext: EvalContext = {
      graph: nextGraph,
      state: this.domainState,
      processes: this.processes,
      events: this.events,
    };
    const violations = checkSafety(this.safetyRules, safetyContext, {
      at: now - this.startedAtMs,
      ...(stepId !== undefined ? { stepId } : {}),
      alreadyFired: this.firedSafety,
    });
    for (const violation of violations) {
      this.firedSafety.add(`${violation.code}@${violation.stepId ?? "-"}`);
      this.bus.emit(ENGINE_EVENTS.safety, violation as unknown as Record<string, unknown>);
      if (violation.knowledgeId) {
        this.bus.emit(ENGINE_EVENTS.knowledge, { id: violation.knowledgeId, source: "safety" });
      }
    }
    this.safetyLog = appendSafety(this.safetyLog, violations);

    if (nextGraph !== graph) this.store.update({ graph: nextGraph });

    // ④ Mission 判定
    this.evaluateMission(safetyContext, now);

    for (const processId of ticked.completed) {
      this.bus.emit("process.completed", { processId });
    }

    return this.snapshot(now);
  }

  private evaluateMission(context: EvalContext, now: number): void {
    const mission = this.mission;
    if (!mission || mission.getState().completed) return;

    // 一次 settle 里连续推进多步：学生可能一个动作同时满足两步的条件
    // （比如接好导管的同时气路也通了）。不循环的话会表现为「要再动一下才前进」。
    for (let guard = 0; guard < mission.getSteps().length; guard += 1) {
      const before = mission.getState().currentIndex;
      const transition = mission.evaluate(context, now);

      if (transition.verdict === "passed") {
        this.bus.emit(ENGINE_EVENTS.stepPassed, {
          stepId: mission.getSteps()[before]?.id,
          at: now - this.startedAtMs,
        });
        if (transition.state.completed) {
          this.bus.emit(ENGINE_EVENTS.missionCompleted, { at: now - this.startedAtMs });
          return;
        }
        continue;
      }

      if (transition.verdict === "failed") {
        this.bus.emit(ENGINE_EVENTS.stepFailed, { stepId: mission.currentStep()?.id });
        for (const id of transition.knowledge) {
          this.bus.emit(ENGINE_EVENTS.knowledge, { id, source: "step_error" });
        }
      } else if (transition.verdict === "blocked") {
        this.bus.emit(ENGINE_EVENTS.stepBlocked, { stepId: mission.currentStep()?.id });
      }
      return;
    }
  }

  private snapshot(now: number): RuntimeSnapshot {
    const mission = this.mission;
    const mode = this.store.getState().mode;
    const graph = this.store.getState().graph;

    if (!mission || !graph) return this.emptySnapshot();

    const context: EvalContext = {
      graph,
      state: this.domainState,
      processes: this.processes,
      events: this.events,
    };

    return {
      stepId: mission.currentStep()?.id ?? null,
      stepIndex: mission.getState().currentIndex,
      stepCount: mission.getSteps().length,
      completed: mission.getState().completed,
      hint: mission.hint(mode, now),
      progress: mission.progress(context),
      processes: this.processes,
      safety: this.safetyLog,
      animations: [],
      knowledge: [],
    };
  }

  private emptySnapshot(): RuntimeSnapshot {
    return {
      stepId: null,
      stepIndex: 0,
      stepCount: 0,
      completed: false,
      hint: null,
      progress: { met: 0, total: 0 },
      processes: this.processes,
      safety: this.safetyLog,
      animations: [],
      knowledge: [],
    };
  }

  getMission(): MissionEngine | null {
    return this.mission;
  }

  getKnowledge(): KnowledgeEngine {
    return this.knowledge;
  }

  getSafetyLog(): SafetyLog {
    return this.safetyLog;
  }

  /** 当前步骤期望的关系，供吸附评分的「当前任务」维度使用（§5.3）。 */
  desiredRelationKeys(): ReadonlySet<string> {
    return this.mission?.desiredRelationKeys() ?? new Set();
  }

  private buildFromPack(pack: ExperimentPack): { scene: Scene; graph: AssemblyGraph } {
    const scene = this.sceneManager.build(pack.scene);
    const objects = this.sceneManager.placeEquipment(pack.equipment);
    const graph = pack.target
      ? createAssemblyGraph(objects, pack.target)
      : createAssemblyGraph(objects);
    return { scene, graph };
  }

  private requireLoaded(action: string): ExperimentPack {
    if (!this.pack) {
      throw new Error(`LabEngine.${action}() 必须在 load() 之后调用`);
    }
    return this.pack;
  }
}
